import { IPlayerAdapter, PlayerInfo, VideoMetadataType, PlatformType } from '@/services/conviva/conviva.types';
import ConvivaHtml5Module from '@convivainc/conviva-js-html5';
import Conviva, { VideoAnalytics } from '@convivainc/conviva-js-coresdk';
import Hls from 'hls.js';
import { deviceDetector } from '@/services/conviva/device-detector';
import { detectPlatform } from '@/utils/device';
import packageJson from '../../../../package.json';

function getPlatformPlayerName(): string {
  const platform = deviceDetector.deviceMetadata.platform;
  switch (platform) {
    case PlatformType.Tizen:
      return 'Thmanyah_Tizen';
    case PlatformType.WebOS:
      return 'Thmanyah_WebOS';
    case PlatformType.Vidaa:
      return 'Thmanyah_Vidaa';
    case PlatformType.Web:
    default:
      return 'Thmanyah_TV_WEB_EMULATOR';
  }
}

export class HLSAdapter implements IPlayerAdapter {
  player: Hls | null = null;
  videoElement: HTMLVideoElement | null = null;
  videoAnalytics: VideoAnalytics | null = null;
  private cleanUpCallbacks: Array<() => void> = [];
  private logging = false;
  private isLiveContent: boolean = false;

  private _avgBrWeightedSum = 0;
  private _avgBrTotalMs = 0;
  private _avgBrIntervalKbps = 0;
  private _avgBrIntervalTs = 0;

  private _resetAvgBitrate() {
    this._avgBrWeightedSum = 0;
    this._avgBrTotalMs = 0;
    this._avgBrIntervalKbps = 0;
    this._avgBrIntervalTs = 0;
  }

  private _reportBitrate(kbps: number) {
    if (!this.videoAnalytics || kbps <= 0) return;
    const nowMs = Date.now();
    if (this._avgBrIntervalTs > 0 && this._avgBrIntervalKbps > 0) {
      const elapsed = nowMs - this._avgBrIntervalTs;
      this._avgBrWeightedSum += this._avgBrIntervalKbps * elapsed;
      this._avgBrTotalMs += elapsed;
    }
    this._avgBrIntervalKbps = kbps;
    this._avgBrIntervalTs = nowMs;
    this.videoAnalytics.reportPlaybackMetric(Conviva.Constants.Playback.BITRATE, kbps);
    const avgKbps = this._avgBrTotalMs > 0 ? Math.round(this._avgBrWeightedSum / this._avgBrTotalMs) : kbps;
    this.videoAnalytics.reportPlaybackMetric(Conviva.Constants.Playback.AVG_BITRATE, avgKbps);
  }

  async createSession({
    assetName = 'NA',
    viewerId,
    isLive = false,
  }: {
    assetName?: string;
    viewerId?: string;
    isLive?: boolean;
  }) {
    await deviceDetector.ready;
    if (process.env.NODE_ENV !== 'production') {
      const platform = typeof window !== 'undefined' ? detectPlatform() : 'web';
      const isTvPlatform = ['webos', 'tizen', 'vidaa'].includes(platform);
      const usingTestGateway = !!(process.env.VUE_APP_CONVIVA_GATEWAY_URL || '').trim();
      // Only skip on non-TV when a test gateway is configured (avoids TLS issues on localhost).
      // When using the prod Conviva endpoint (no gateway), allow all platforms including web.
      if (!isTvPlatform && usingTestGateway) {
        if (this.logging) {
          console.warn('[conviva][hls] Skipping Conviva session: non-TV + test gateway');
        }
        return;
      }
    }

    try {
      const trimmedName = assetName?.trim() || '';
      let validAssetName = trimmedName;
      if (!trimmedName || trimmedName.toUpperCase() === 'NA') {
        console.warn(`[conviva][HLSAdapter] Invalid assetName: "${assetName}". Using fallback.`);
        validAssetName = 'Unknown Asset';
      }

      this.isLiveContent = isLive;

      const settings: {
        [Conviva.Constants.GATEWAY_URL]?: string;
        [Conviva.Constants.LOG_LEVEL]?: number;
      } = {};

      if (process.env.VUE_APP_CONVIVA_GATEWAY_URL) {
        settings[Conviva.Constants.GATEWAY_URL] = process.env.VUE_APP_CONVIVA_GATEWAY_URL || '';
      }

      if (this.logging) {
        settings[Conviva.Constants.LOG_LEVEL] = Conviva.Constants.LogLevel.DEBUG;
      }

      Conviva.Analytics.init(process.env.VUE_APP_CONVIVA_CUSTOMER_KEY || '', null, settings);

      Conviva.Analytics.reportDeviceMetric(
        Conviva.Constants.Network.CONNECTION_TYPE,
        deviceDetector.deviceMetadata.connectionType || 'WiFi',
      );

      Conviva.Analytics.setDeviceMetadata({
        [Conviva.Constants.DeviceMetadata.BRAND]: deviceDetector.deviceMetadata.brand,
        [Conviva.Constants.DeviceMetadata.MANUFACTURER]: deviceDetector.deviceMetadata.manufacturer,
        [Conviva.Constants.DeviceMetadata.MODEL]: deviceDetector.deviceMetadata.deviceModel,
        [Conviva.Constants.DeviceMetadata.TYPE]: Conviva.Constants.DeviceType.SMARTTV,
        [Conviva.Constants.DeviceMetadata.OS_NAME]: deviceDetector.deviceMetadata.osName,
        [Conviva.Constants.DeviceMetadata.OS_VERSION]: deviceDetector.deviceMetadata.osVersion,
        [Conviva.Constants.DeviceMetadata.CATEGORY]:
          deviceDetector.deviceMetadata.platform === PlatformType.Web
            ? Conviva.Constants.DeviceCategory.WEB
            : Conviva.Constants.DeviceCategory.SMART_TV,
      });

      this.videoAnalytics = Conviva.Analytics.buildVideoAnalytics();

      // Set framework info before reportPlaybackRequested so it is included in the opening heartbeat
      this.videoAnalytics.setPlayerInfo({
        [Conviva.Constants.FRAMEWORK_NAME]: 'HLS.js',
        [Conviva.Constants.FRAMEWORK_VERSION]: Hls.version || '1.0.0',
      });

      this.videoAnalytics.reportPlaybackRequested({
        [Conviva.Constants.ASSET_NAME]: validAssetName,
        [Conviva.Constants.VIEWER_ID]: viewerId || 'NA',
        [Conviva.Constants.IS_LIVE]: isLive ? 'LIVE' : 'VOD',
        [Conviva.Constants.APPLICATION_VERSION]: packageJson.version,
        [Conviva.Constants.PLAYER_NAME]: getPlatformPlayerName(),
        [Conviva.Constants.FRAMEWORK_NAME]: 'HLS.js',
        [Conviva.Constants.FRAMEWORK_VERSION]: Hls.version || '1.0.0',
      });
    } catch (error) {
      if (this.logging) {
        console.warn('[conviva][hls] Failed to initialize Conviva session:', error);
      }
      this.videoAnalytics = null;
    }
  }

  destroySession() {
    if (!this.videoAnalytics) {
      return;
    }
    this._resetAvgBitrate();
    this.videoAnalytics.release();
    this.videoAnalytics = null;
    this.cleanUpCallbacks.forEach(callback => callback());
    this.cleanUpCallbacks = [];
  }

  setPlayer(playerAndElement: { player: Hls; videoElement: HTMLVideoElement }) {
    if (!this.videoAnalytics) {
      return;
    }

    this.player = playerAndElement.player;
    this.videoElement = playerAndElement.videoElement;

    setTimeout(() => {
      if (!this.videoAnalytics || !this.videoElement) {
        return;
      }
      this.videoAnalytics.setPlayer(this.videoElement, {
        [Conviva.Constants.CONVIVA_MODULE]: ConvivaHtml5Module,
      });
    }, 0);

    const normalizeErrorMessage = (err: unknown) => {
      if (err instanceof Error) return err.message;
      if (typeof err === 'string') return err;
      try {
        return JSON.stringify(err);
      } catch {
        return String(err);
      }
    };

    const onError = (event: any, data: any) => {
      if (!this.videoAnalytics) return;
      if (data?.fatal) {
        this.videoAnalytics.reportPlaybackError(data?.message || data?.details || normalizeErrorMessage(data || event));
      }
    };

    const bitsToKbps = (bps: number) => Math.max(0, Math.round(bps / 1000));

    const onLevelSwitched = (_event: any, data: any) => {
      if (!this.videoAnalytics || !this.player) return;
      const level = this.player.levels[data.level];
      if (level?.bitrate) {
        this._reportBitrate(bitsToKbps(level.bitrate));
      }
    };

    const onManifestParsed = (_event: any, data: any) => {
      if (!this.videoAnalytics || !this.player) return;
      const streamUrl = this.player.url || '';
      this.videoAnalytics.setContentInfo({
        [Conviva.Constants.IS_LIVE]: this.isLiveContent ? 'LIVE' : 'VOD',
        [Conviva.Constants.DURATION]: this.videoElement?.duration || 0,
        [Conviva.Constants.STREAM_URL]: streamUrl,
      });
      // currentLevel is -1 at manifest parse time — report the first available level as the
      // starting bitrate so Conviva doesn't record 0 bps before the first fragment loads
      if (data.levels && data.levels.length > 0) {
        const idx = this.player.currentLevel >= 0 ? this.player.currentLevel : 0;
        const level = data.levels[idx];
        if (level?.bitrate) {
          this._reportBitrate(bitsToKbps(level.bitrate));
        }
      }
    };

    const onLevelLoading = (_event: any, data: any) => {
      if (!this.videoAnalytics || !this.player) return;
      const level = this.player.levels[data.level];
      if (level?.bitrate) {
        this._reportBitrate(bitsToKbps(level.bitrate));
      }
    };

    const onFragLoaded = () => {
      if (!this.videoAnalytics || !this.player || this.player.currentLevel === -1) return;
      const level = this.player.levels[this.player.currentLevel];
      if (level?.bitrate) {
        this._reportBitrate(bitsToKbps(level.bitrate));
      }
    };

    this.player.on((Hls as any).Events.ERROR, onError);
    this.player.on((Hls as any).Events.LEVEL_SWITCHED, onLevelSwitched);
    this.player.on((Hls as any).Events.LEVEL_LOADING, onLevelLoading);
    this.player.on((Hls as any).Events.MANIFEST_PARSED, onManifestParsed);
    this.player.on((Hls as any).Events.FRAG_LOADED, onFragLoaded);

    // Periodic bitrate report every 5s so Conviva has enough samples to compute avg bitrate
    const brTimer = setInterval(() => {
      if (!this.videoAnalytics || !this.player || this.player.currentLevel === -1) return;
      const level = this.player.levels[this.player.currentLevel];
      if (level?.bitrate) {
        this._reportBitrate(bitsToKbps(level.bitrate));
      }
    }, 5000);

    // Framerate sampling every 2s via getVideoPlaybackQuality()
    let _lastFrameCount = 0;
    let _lastFrameTime = 0;
    let _frFirstTick = true;
    const frTimer = setInterval(() => {
      if (!this.videoAnalytics || !this.videoElement) return;
      if (typeof this.videoElement.getVideoPlaybackQuality !== 'function') return;
      const q = this.videoElement.getVideoPlaybackQuality();
      const now = performance.now();
      if (_frFirstTick) {
        _frFirstTick = false;
        _lastFrameCount = q.totalVideoFrames;
        _lastFrameTime = now;
        return;
      }
      if (_lastFrameTime > 0 && q.totalVideoFrames > _lastFrameCount) {
        const elapsed = (now - _lastFrameTime) / 1000;
        const fps = Math.round((q.totalVideoFrames - _lastFrameCount) / elapsed);
        if (fps > 0) {
          this.videoAnalytics.reportPlaybackMetric(Conviva.Constants.Playback.RENDERED_FRAMERATE, fps);
        }
      }
      _lastFrameCount = q.totalVideoFrames;
      _lastFrameTime = now;
    }, 2000);

    this.cleanUpCallbacks.push(() => {
      clearInterval(brTimer);
      clearInterval(frTimer);
      if (this.player) {
        this.player.off((Hls as any).Events.ERROR, onError);
        this.player.off((Hls as any).Events.LEVEL_SWITCHED, onLevelSwitched);
        this.player.off((Hls as any).Events.LEVEL_LOADING, onLevelLoading);
        this.player.off((Hls as any).Events.MANIFEST_PARSED, onManifestParsed);
        this.player.off((Hls as any).Events.FRAG_LOADED, onFragLoaded);
      }
    });
  }

  unsetPlayer() {
    this.player = null;
    this.videoElement = null;
  }

  reportAppBackgrounded(): void {
    try {
      Conviva.Analytics.reportAppBackgrounded();
    } catch {
      // ignore
    }
  }

  reportAppForegrounded(): void {
    try {
      Conviva.Analytics.reportAppForegrounded();
    } catch {
      // ignore
    }
  }

  sendCustomEvent(eventName: string, attributes: Record<string, string>): void {
    if (!this.videoAnalytics) return;
    try {
      this.videoAnalytics.reportPlaybackEvent(eventName, attributes);
    } catch (error) {
      if (this.logging) {
        console.warn('[conviva][HLSAdapter] Failed to send custom event', eventName, error);
      }
    }
  }

  getPlayerInfo(): PlayerInfo {
    return {
      name: 'HLS.js',
      version: Hls.version || '1.0.0',
    };
  }

  setContentInfo(metadata: VideoMetadataType) {
    if (!this.videoAnalytics) return;
    this.isLiveContent = metadata.isLive || false;

    const trimmedTitle = metadata.title?.trim() || '';
    const validAssetName = trimmedTitle && trimmedTitle.toUpperCase() !== 'NA' ? trimmedTitle : 'Unknown Asset';

    const streamUrl = this.player?.url || '';
    const contentInfo: any = {
      [Conviva.Constants.IS_LIVE]: this.isLiveContent ? 'LIVE' : 'VOD',
      [Conviva.Constants.ASSET_NAME]: validAssetName,
      [Conviva.Constants.VIEWER_ID]: String(metadata.viewerId) || 'NA',
    };
    if (streamUrl) {
      contentInfo[Conviva.Constants.STREAM_URL] = streamUrl;
    }
    if (metadata.custom) {
      Object.assign(contentInfo, metadata.custom);
    }
    this.videoAnalytics.setContentInfo(contentInfo);
  }
}
